import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Clock, RefreshCw, Loader2, AlertTriangle, Timer, Hourglass } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const THRESHOLD_MIN = 90;

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Error";
  if (typeof d === "string") return d;
  return d.message_en || d.message_ar || JSON.stringify(d);
};

const fmtTs = (ts, lang) => {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString(lang === "ar" ? "ar-EG" : "en-US", {
      dateStyle: "short", timeStyle: "short",
    });
  } catch { return ts; }
};

export default function VisitDurationReport({ range, branch }) {
  const { t, lang } = useLanguage();
  const isAr = lang === "ar";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (range?.start) q.set("start", range.start);
      if (range?.end) q.set("end", range.end);
      if (branch) q.set("branch", branch);
      const r = await api.get(`/reports/visit-duration?${q}`);
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.start, range?.end, branch]);

  const summary = data?.summary || {};
  const rows = data?.rows || [];

  const stateLabel = (s) => {
    const m = {
      completed: isAr ? "مكتمل" : "Completed",
      waiting: t("waiting"),
      in_service: t("in_service"),
      in_progress: t("in_progress"),
      missing_arrival: t("missing_arrival"),
      cancelled: isAr ? "ملغي" : "Cancelled",
    };
    return m[s] || s;
  };

  const stateBadgeClass = (s) => {
    if (s === "waiting") return "bg-amber-100 text-amber-800";
    if (s === "in_service" || s === "in_progress") return "bg-violet-100 text-violet-800";
    if (s === "missing_arrival") return "bg-rose-100 text-rose-700";
    if (s === "cancelled") return "bg-stone-100 text-stone-600";
    return "bg-green-100 text-green-700";
  };

  const fmtMin = (v) => (v != null ? `${v} ${isAr ? "د" : "min"}` : "—");

  return (
    <div className="space-y-5" data-testid="visit-duration-report">
      <div className="flex justify-end no-print">
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2"
          data-testid="visit-duration-refresh"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {t("refresh") || (isAr ? "تحديث" : "Refresh")}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: t("visit_duration_avg"), value: fmtMin(summary.avg_minutes), icon: Clock },
          { label: t("avg_waiting"), value: fmtMin(summary.avg_waiting_minutes), icon: Hourglass },
          { label: t("avg_service"), value: fmtMin(summary.avg_service_minutes), icon: Timer },
          { label: t("visit_duration_max"), value: fmtMin(summary.max_minutes), icon: Timer },
          { label: t("visit_duration_count"), value: summary.count ?? 0, icon: CheckIcon },
          { label: t("waiting"), value: summary.waiting_live ?? 0, icon: Hourglass },
          { label: t("in_service"), value: summary.in_service_live ?? 0, icon: Loader2 },
        ].map(({ label, value, icon: Icon }) => (
          <div key={label} className="card-surface p-4">
            <div className="flex items-center gap-2 text-[var(--text-muted)] text-xs mb-1">
              <Icon size={14} className="text-[var(--gold)]" />
              {label}
            </div>
            <div className="font-heading text-2xl gold-text num-display">{value}</div>
          </div>
        ))}
      </div>

      <div className="card-surface overflow-auto">
        <table className="table-dark w-full text-sm">
          <thead>
            <tr>
              <th>{t("customer")}</th>
              <th>{t("date")}</th>
              <th>{t("time")}</th>
              <th>{isAr ? "الموظف" : "Staff"}</th>
              <th>{t("customer_arrived")}</th>
              <th>{t("service_started")}</th>
              <th>{isAr ? "وقت الدفع" : "Paid at"}</th>
              <th>{t("waiting_time")}</th>
              <th>{t("service_duration")}</th>
              <th>{t("visit_duration")}</th>
              <th>{t("status")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={11} className="text-center py-10 text-[var(--text-muted)]">
                  {t("no_data")}
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const slow = r.row_state === "completed" && r.visit_duration_minutes > THRESHOLD_MIN;
              return (
                <tr
                  key={r.booking_id}
                  data-testid={`visit-row-${r.booking_id}`}
                  className={slow ? "bg-amber-50/80" : ""}
                >
                  <td>
                    <div className="font-medium">{r.customer_name}</div>
                    {r.customer_phone && <div className="text-xs text-[var(--text-muted)]" dir="ltr">{r.customer_phone}</div>}
                  </td>
                  <td className="num-display">{r.date || "—"}</td>
                  <td className="num-display">{r.time_slot || "—"}</td>
                  <td>{r.staff_name || "—"}</td>
                  <td className="num-display text-xs">{fmtTs(r.arrived_at, lang)}</td>
                  <td className="num-display text-xs">{fmtTs(r.service_started_at, lang)}</td>
                  <td className="num-display text-xs">{fmtTs(r.paid_at, lang)}</td>
                  <td className="num-display">{fmtMin(r.waiting_minutes)}</td>
                  <td className="num-display">{fmtMin(r.service_duration_minutes)}</td>
                  <td className="num-display font-semibold">{fmtMin(r.visit_duration_minutes)}</td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${stateBadgeClass(r.row_state)}`}>
                      {stateLabel(r.row_state)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {rows.some((r) => r.row_state === "completed" && r.visit_duration_minutes > THRESHOLD_MIN) && (
        <p className="text-xs text-amber-700 flex items-center gap-1">
          <AlertTriangle size={12} />
          {isAr
            ? `الصفوف المظللة تجاوزت ${THRESHOLD_MIN} دقيقة — راجع جودة الخدمة.`
            : `Highlighted rows exceeded ${THRESHOLD_MIN} minutes — review for quality.`}
        </p>
      )}
    </div>
  );
}

function CheckIcon(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
